// Marketplace half of the one-shot contextual-entry handoff. A surface
// stages a single ConciergeMessageSeed here, then navigates to the clean
// /concierge route. Consumer-client's consume-on-mount effect atomically
// reads and deletes it exactly once through the bridge action below.
//
// Session-scoped only (never falls back to localStorage), via the shared
// persisted-values helpers rather than touching sessionStorage directly.
import { getSessionPersistedValue, removePersistedValue, setPersistedValue } from 'shared/utils/persisted-values';

import { ConciergeMessageSeed } from './contract/actions';

// Seeds with longer messages are rejected; builders use this to avoid staging them.
export const MAX_CONCIERGE_SEED_MESSAGE_LENGTH = 500;

const STORAGE_KEY = 'concierge:message-seed';

// Short-lived on purpose: this only needs to survive the click → route-push →
// MF-remote-mount window, not a real session. A stale, unconsumed entry from
// an abandoned navigation should not resurrect itself on a later, unrelated
// concierge visit.
const TTL_MS = 60_000;

type StoredConciergeMessageSeed = {
  seed: ConciergeMessageSeed;
  dispensaryId: string;
  expiresAt: number;
};

type SeedValidatorMap = {
  [K in ConciergeMessageSeed['kind']]: (
    candidate: Record<string, unknown>
  ) => Extract<ConciergeMessageSeed, { kind: K }> | null;
};

function sanitizeMessage(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_CONCIERGE_SEED_MESSAGE_LENGTH) return undefined;
  return trimmed;
}

// Serves as both the runtime kind lookup and compile-time coverage check. A
// new ConciergeMessageSeed kind without an entry here is a type error.
const seedValidators: SeedValidatorMap = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  product_search: (candidate) => {
    const { message, context } = candidate;
    const trimmedMessage = sanitizeMessage(message);
    if (!trimmedMessage) return null;

    return {
      kind: 'product_search',
      message: trimmedMessage,
      ...(isRecord(context) ? { context } : {}),
    };
  },
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isSeedKind(kind: string): kind is keyof typeof seedValidators {
  return Object.prototype.hasOwnProperty.call(seedValidators, kind);
}

/** Runtime shape guard for a value just parsed out of storage. */
function validateConciergeMessageSeed(value: unknown): ConciergeMessageSeed | null {
  if (!isRecord(value)) return null;

  const { kind } = value;
  if (typeof kind !== 'string' || !isSeedKind(kind)) return null;

  return seedValidators[kind](value);
}

function parseStoredConciergeMessageSeed(
  value: unknown,
  currentDispensaryId: string | null | undefined
): ConciergeMessageSeed | null {
  if (!isRecord(value)) return null;

  const { dispensaryId, expiresAt, seed } = value;
  if (
    typeof dispensaryId !== 'string' ||
    !currentDispensaryId ||
    dispensaryId !== currentDispensaryId ||
    typeof expiresAt !== 'number' ||
    !Number.isFinite(expiresAt) ||
    expiresAt <= Date.now()
  ) {
    return null;
  }

  return validateConciergeMessageSeed(seed);
}

/**
 * Stage one seed for the next Concierge mount to consume. Overwrites any
 * prior unconsumed entry — only the most recent contextual click should win.
 */
export function writeConciergeMessageSeed(seed: ConciergeMessageSeed, dispensaryId: string | null | undefined): void {
  if (!dispensaryId) return;
  const stored: StoredConciergeMessageSeed = {
    seed,
    dispensaryId,
    expiresAt: Date.now() + TTL_MS,
  };
  setPersistedValue(STORAGE_KEY, stored, true);
}

/**
 * Atomically consume the staged seed: the entry is deleted unconditionally,
 * regardless of whether it turns out to be missing, malformed, expired, or
 * scoped to a different dispensary. That guarantees a second call (a
 * rerender, a remount, a duplicate bridge-action invocation) always gets
 * null — there is nothing left to consume twice.
 */
export function consumeInitialConciergeMessageSeed(
  currentDispensaryId: string | null | undefined
): ConciergeMessageSeed | null {
  const stored: unknown = getSessionPersistedValue(STORAGE_KEY, null);
  removePersistedValue(STORAGE_KEY, true);

  return parseStoredConciergeMessageSeed(stored, currentDispensaryId);
}
